import { toPersian, formatPersianDateLong } from '../../utils/persianNumber';
import { BASE_MEDIA_URL } from '../../api/client';

const PRINT_STYLE = `
@media print {
  body * { visibility: hidden !important; }
  #finance-invoice-print-sheet, #finance-invoice-print-sheet * { visibility: visible !important; }
  #finance-invoice-print-sheet {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
  @page { size: A4 portrait; margin: 12mm; }
}
#finance-invoice-print-sheet {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 210mm;
  min-height: 297mm;
  background: #fff;
  color: #111;
  direction: rtl;
  font-family: Tahoma, Arial, sans-serif;
  box-sizing: border-box;
}
.fip-brand-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #1B3068 0%, #233A7A 45%, #29AAE1 100%);
  color: #fff;
}
.fip-brand-logo-wrap {
  width: 52px; height: 52px; border-radius: 12px;
  background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.fip-brand-logo { width: 34px; height: 34px; border-radius: 8px; object-fit: contain; }
.fip-brand-text { flex: 1; text-align: center; }
.fip-brand-org { font-size: 16px; font-weight: 800; margin: 0 0 3px; }
.fip-brand-sub { font-size: 11px; margin: 0; opacity: 0.9; }
.fip-brand-badge {
  font-size: 10px; font-weight: 700; padding: 6px 10px; border-radius: 8px;
  background: rgba(255,255,255,0.18); border: 1px solid rgba(255,255,255,0.22);
  white-space: nowrap;
}
.fip-body { padding: 12mm 12mm 10mm; }
.fip-title-wrap {
  text-align: center; margin: 0 0 14px; padding-bottom: 10px;
  border-bottom: 2px solid #1B3068;
}
.fip-title { font-size: 18px; font-weight: 800; margin: 0 0 4px; color: #0f172a; }
.fip-number { font-size: 14px; font-weight: 700; color: #1B3068; margin: 0; }
.fip-meta {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; font-size: 12px;
}
.fip-meta-box {
  border: 1px solid #B7C9EC; border-radius: 8px; padding: 10px 12px; background: #f8fbff;
}
.fip-meta-label { font-size: 10px; color: #64748b; font-weight: 700; margin-bottom: 4px; }
.fip-meta-value { font-size: 12px; font-weight: 600; color: #0f172a; line-height: 1.6; }
.fip-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 18px; }
.fip-table th, .fip-table td { border: 1px solid #cbd5e1; padding: 10px 12px; text-align: right; }
.fip-table th { background: #EDF3FC; font-weight: 700; color: #1e40af; }
.fip-amount-row td { font-size: 14px; font-weight: 800; background: #f0fdf4; }
.fip-signatures {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; margin-top: 24px;
}
.fip-sign-box {
  border: 1px solid #64748b; border-radius: 8px; padding: 10px; min-height: 88px;
  display: flex; flex-direction: column; justify-content: space-between; text-align: center; font-size: 11px;
}
.fip-sign-label { font-weight: 700; color: #334155; margin-bottom: 4px; }
.fip-sign-line { border-bottom: 1px dashed #94a3b8; height: 46px; margin-top: 8px; }
.fip-sign-img { height: 46px; margin-top: 8px; display: flex; align-items: center; justify-content: center; }
.fip-sign-img img { max-height: 44px; max-width: 100%; object-fit: contain; }
.fip-sign-name { border-top: 1px solid #94a3b8; margin: 8px 8px 0; padding-top: 6px; color: #64748b; font-size: 10px; }
.fip-note { font-size: 11px; color: #64748b; margin-top: 16px; line-height: 1.7; white-space: pre-wrap; }
`;

function formatAmount(amount) {
  if (amount == null) return '—';
  return `${toPersian(Number(amount).toLocaleString('fa-IR'))} ریال`;
}

export default function FinanceInvoicePrintForm({ invoice }) {
  if (!invoice) return null;
  const logoSrc = `${window.location.origin}/pejvak-mark.svg`;
  const issueLabel = invoice.invoice_issue_date_label
    || (invoice.invoice_issue_date ? formatPersianDateLong(invoice.invoice_issue_date) : '—');
  const financeManagerSignatureUrl = invoice.finance_manager_signature_file
    ? `${BASE_MEDIA_URL}${invoice.finance_manager_signature_file}`
    : null;
  const financeManagerLabel = invoice.finance_manager_name
    ? `امضای مدیر مالی (${invoice.finance_manager_name})`
    : 'امضای مدیر مالی';

  return (
    <>
      <style>{PRINT_STYLE}</style>
      <div id="finance-invoice-print-sheet" aria-hidden="true">
        <div className="fip-brand-bar">
          <div className="fip-brand-logo-wrap">
            <img src={logoSrc} alt="" className="fip-brand-logo" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </div>
          <div className="fip-brand-text">
            <p className="fip-brand-org">{invoice.issuer_company_title || 'شرکت پژواک رایانه نقش جهان'}</p>
            <p className="fip-brand-sub">سامانه مدیریت CRM — واحد مالی</p>
          </div>
          <div className="fip-brand-badge">فاکتور رسمی</div>
        </div>

        <div className="fip-body">
          <div className="fip-title-wrap">
            <h1 className="fip-title">صورتحساب / فاکتور خدمات</h1>
            {invoice.invoice_number && (
              <p className="fip-number">شماره فاکتور: {toPersian(invoice.invoice_number)}</p>
            )}
          </div>

          <div className="fip-meta" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            <div className="fip-meta-box">
              <div className="fip-meta-label">تاریخ</div>
              <div className="fip-meta-value">{issueLabel}</div>
            </div>
            <div className="fip-meta-box">
              <div className="fip-meta-label">گیرنده</div>
              <div className="fip-meta-value">{invoice.company_display || invoice.company_name}</div>
            </div>
            <div className="fip-meta-box">
              <div className="fip-meta-label">مسئول گیرنده</div>
              <div className="fip-meta-value">
                {invoice.receiver_manager_name || invoice.receiver_responsible_name || '—'}
              </div>
            </div>
          </div>

          <table className="fip-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>ردیف</th>
                <th>شرح خدمات / کالا</th>
                <th style={{ width: 120 }}>مبلغ (ریال)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{toPersian(1)}</td>
                <td>
                  <strong>{invoice.item_title}</strong>
                  {invoice.product_code && (
                    <div style={{ marginTop: 4, fontSize: 10, color: '#64748b' }}>
                      کد کالا: {toPersian(invoice.product_code)}
                      {invoice.product_name ? ` — ${invoice.product_name}` : ''}
                    </div>
                  )}
                  {invoice.item_description && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#475569', lineHeight: 1.6 }}>
                      {invoice.item_description}
                    </div>
                  )}
                </td>
                <td>{formatAmount(invoice.amount)}</td>
              </tr>
              <tr className="fip-amount-row">
                <td colSpan={2}>جمع کل قابل پرداخت</td>
                <td>{formatAmount(invoice.amount)}</td>
              </tr>
            </tbody>
          </table>

          {invoice.finance_note && (
            <div className="fip-note">
              <strong>توضیحات مالی:</strong>
              {' '}
              {invoice.finance_note}
            </div>
          )}

          <div className="fip-signatures">
            <div className="fip-sign-box">
              <div className="fip-sign-label">{financeManagerLabel}</div>
              {financeManagerSignatureUrl ? (
                <div className="fip-sign-img">
                  <img src={financeManagerSignatureUrl} alt="" />
                </div>
              ) : (
                <div className="fip-sign-line" />
              )}
              {invoice.finance_manager_name && (
                <div className="fip-sign-name">{invoice.finance_manager_name}</div>
              )}
            </div>
            <div className="fip-sign-box">
              <div className="fip-sign-label">امضای مدیر شرکت گیرنده</div>
              <div className="fip-sign-line" />
              {invoice.receiver_manager_name && (
                <div className="fip-sign-name">{invoice.receiver_manager_name}</div>
              )}
            </div>
            <div className="fip-sign-box">
              <div className="fip-sign-label">امضای مسئول / نماینده گیرنده</div>
              <div className="fip-sign-line" />
              {invoice.receiver_responsible_name && (
                <div className="fip-sign-name">{invoice.receiver_responsible_name}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
